// Blog Posts Data - Multilingual
// Automatische Veröffentlichung basierend auf datePublished
// Neue Artikel erscheinen automatisch am geplanten Datum

import { articles2026DE, articles2026EN } from './blogArticles2026';
import { articles2026bDE, articles2026bEN } from './blogArticles2026b';
import { articles2027DE, articles2027EN } from './blogArticles2027';

// Basis-Artikel (bereits veröffentlicht)
const baseArticlesDE = [
    {
      id: 'was-ist-sentiment-analyse',
      slug: 'was-ist-sentiment-analyse',
      title: 'Was ist Sentiment Analyse im Trading?',
      description: 'Erfahren Sie, wie Sentiment Analyse funktioniert und wie Sie diese für bessere Trading-Entscheidungen nutzen können.',
      content: `
## Was ist Sentiment Analyse?

Sentiment Analyse im Trading ist eine Methode zur Messung der Marktstimmung. Sie analysiert, wie Trader aktuell positioniert sind - ob sie mehrheitlich auf steigende (long) oder fallende (short) Kurse setzen.

### Warum ist Sentiment wichtig?

Die Marktstimmung kann ein wertvoller Indikator sein, weil:

1. **Extreme Positionierungen** oft auf mögliche Trendwenden hindeuten
2. **Die Masse häufig falsch liegt** - wenn alle long sind, fehlen Käufer
3. **Sentiment-Daten** zusätzliche Einblicke neben technischer Analyse bieten

### Wie nutzt man Sentiment-Daten?

**Als Kontraindikator:** Wenn beispielsweise 80% der Retail-Trader long positioniert sind, kann dies ein Warnsignal sein. Die Mehrheit der Retail-Trader verliert langfristig Geld - gegen die Masse zu handeln kann daher sinnvoll sein.

**Zur Bestätigung:** Stimmt das Sentiment mit Ihrer technischen Analyse überein, erhöhen sich die Erfolgschancen eines Trades.

### Sentmo Sentiment-Daten

Bei Sentmo erhalten Sie tägliche Sentiment-Daten für wichtige Instrumente wie:
- US30 (Dow Jones)
- NASDAQ
- S&P500
- DAX (GER30)
- Gold

Nutzen Sie diese Daten als zusätzliches Werkzeug in Ihrer Trading-Strategie.
      `,
      category: 'Grundlagen',
      datePublished: '2026-01-15',
      dateModified: '2026-01-30',
      readTime: '5 min',
      featured: true
    },
    {
      id: 'trading-journal-vorteile',
      slug: 'trading-journal-vorteile',
      title: 'Warum jeder Trader ein Trading Journal führen sollte',
      description: 'Ein Trading Journal ist eines der wichtigsten Werkzeuge für kontinuierliche Verbesserung. Lernen Sie die Vorteile kennen.',
      content: `
## Die Kraft des Trading Journals

Ein Trading Journal ist mehr als nur eine Liste Ihrer Trades. Es ist ein Werkzeug zur Selbstreflexion und kontinuierlichen Verbesserung.

### Was gehört in ein Trading Journal?

1. **Entry und Exit Punkte** - Wann und warum sind Sie eingestiegen?
2. **Positionsgröße** - Wie viel Risiko sind Sie eingegangen?
3. **Emotionaler Zustand** - Wie haben Sie sich gefühlt?
4. **Marktbedingungen** - Was war das übergeordnete Bild?
5. **Ergebnis und Lessons Learned** - Was können Sie mitnehmen?

### Vorteile eines Trading Journals

**Muster erkennen:** Nach einigen Wochen sehen Sie, welche Setups bei Ihnen funktionieren und welche nicht.

**Emotionen kontrollieren:** Das Aufschreiben hilft, emotionale Entscheidungen zu reduzieren.

**Disziplin stärken:** Wer dokumentiert, hält sich eher an seine Regeln.

**Performance messen:** Ohne Daten keine Verbesserung.

### Tipps für den Start

- Beginnen Sie einfach - selbst eine Excel-Tabelle reicht
- Seien Sie ehrlich zu sich selbst
- Reviewen Sie regelmäßig (wöchentlich)
- Sentmo plant ein integriertes Trading Journal - bleiben Sie gespannt!
      `,
      category: 'Praxis',
      datePublished: '2026-01-20',
      dateModified: '2026-01-30',
      readTime: '4 min',
      featured: true
    },
    {
      id: 'contrarian-trading-strategie',
      slug: 'contrarian-trading-strategie',
      title: 'Contrarian Trading: Gegen die Masse handeln',
      description: 'Lernen Sie die Contrarian-Strategie kennen und wie Sentiment-Daten dabei helfen können.',
      content: `
## Was ist Contrarian Trading?

Contrarian Trading bedeutet, gegen die vorherrschende Marktmeinung zu handeln. Wenn die Mehrheit bullish ist, suchen Contrarians nach Short-Möglichkeiten - und umgekehrt.

### Warum funktioniert Contrarian Trading?

Die Statistiken zeigen: Die Mehrheit der Retail-Trader verliert langfristig Geld. Das hat mehrere Gründe:

- **Emotionale Entscheidungen** in Extremsituationen
- **Zu spätes Einsteigen** in bereits laufende Trends
- **Fehlende Risikokontrolle**

Wenn 80% der Trader long sind, bedeutet das auch: Es fehlen potenzielle Käufer, um den Markt weiter nach oben zu treiben.

### Sentiment-Daten als Contrarian-Werkzeug

Bei Sentmo sehen Sie genau, wie Trader positioniert sind. Nutzen Sie diese Daten:

**Warnsignale erkennen:**
- Extreme Long-Positionierung (>75%) = mögliche Korrektur
- Extreme Short-Positionierung (>75%) = mögliche Rally

**Nicht blind handeln:**
Contrarian Trading funktioniert am besten in Kombination mit technischer Analyse. Warten Sie auf Bestätigung durch Price Action.

### Beispiel

Wenn Sie im DAX 78% Long-Positionierung sehen UND der Markt an einem wichtigen Widerstand steht, erhöhen sich die Chancen für eine Korrektur.
      `,
      category: 'Strategie',
      datePublished: '2026-01-25',
      dateModified: '2026-01-30',
      readTime: '6 min',
      featured: false
    }
];

const baseArticlesEN = [
    {
      id: 'what-is-sentiment-analysis',
      slug: 'what-is-sentiment-analysis',
      title: 'What is Sentiment Analysis in Trading?',
      description: 'Learn how sentiment analysis works and how you can use it for better trading decisions.',
      content: `
## What is Sentiment Analysis?

Sentiment analysis in trading is a method for measuring market mood. It analyzes how traders are currently positioned - whether they are predominantly betting on rising (long) or falling (short) prices.

### Why is Sentiment Important?

Market sentiment can be a valuable indicator because:

1. **Extreme positioning** often indicates possible trend reversals
2. **The crowd is often wrong** - when everyone is long, there are no buyers left
3. **Sentiment data** provides additional insights beyond technical analysis

### How to Use Sentiment Data?

**As a contrarian indicator:** For example, if 80% of retail traders are positioned long, this can be a warning signal. The majority of retail traders lose money in the long run - so trading against the crowd can make sense.

**For confirmation:** If sentiment aligns with your technical analysis, the chances of a successful trade increase.

### Sentmo Sentiment Data

At Sentmo, you receive daily sentiment data for important instruments such as:
- US30 (Dow Jones)
- NASDAQ
- S&P500
- DAX (GER30)
- Gold

Use this data as an additional tool in your trading strategy.
      `,
      category: 'Basics',
      datePublished: '2026-01-15',
      dateModified: '2026-01-30',
      readTime: '5 min',
      featured: true
    },
    {
      id: 'trading-journal-benefits',
      slug: 'trading-journal-benefits',
      title: 'Why Every Trader Should Keep a Trading Journal',
      description: 'A trading journal is one of the most important tools for continuous improvement. Learn about the benefits.',
      content: `
## The Power of a Trading Journal

A trading journal is more than just a list of your trades. It's a tool for self-reflection and continuous improvement.

### What Belongs in a Trading Journal?

1. **Entry and Exit Points** - When and why did you enter?
2. **Position Size** - How much risk did you take?
3. **Emotional State** - How were you feeling?
4. **Market Conditions** - What was the bigger picture?
5. **Result and Lessons Learned** - What can you take away?

### Benefits of a Trading Journal

**Recognize patterns:** After a few weeks, you'll see which setups work for you and which don't.

**Control emotions:** Writing things down helps reduce emotional decisions.

**Strengthen discipline:** Those who document are more likely to stick to their rules.

**Measure performance:** Without data, there's no improvement.

### Tips for Getting Started

- Start simple - even an Excel spreadsheet works
- Be honest with yourself
- Review regularly (weekly)
- Sentmo is planning an integrated trading journal - stay tuned!
      `,
      category: 'Practice',
      datePublished: '2026-01-20',
      dateModified: '2026-01-30',
      readTime: '4 min',
      featured: true
    },
    {
      id: 'contrarian-trading-strategy',
      slug: 'contrarian-trading-strategy',
      title: 'Contrarian Trading: Trading Against the Crowd',
      description: 'Learn about the contrarian strategy and how sentiment data can help.',
      content: `
## What is Contrarian Trading?

Contrarian trading means trading against the prevailing market opinion. When the majority is bullish, contrarians look for short opportunities - and vice versa.

### Why Does Contrarian Trading Work?

Statistics show: The majority of retail traders lose money in the long run. There are several reasons for this:

- **Emotional decisions** in extreme situations
- **Entering too late** into already running trends
- **Lack of risk control**

When 80% of traders are long, it also means: There are no potential buyers left to push the market higher.

### Sentiment Data as a Contrarian Tool

At Sentmo, you can see exactly how traders are positioned. Use this data:

**Recognize warning signals:**
- Extreme long positioning (>75%) = possible correction
- Extreme short positioning (>75%) = possible rally

**Don't trade blindly:**
Contrarian trading works best in combination with technical analysis. Wait for confirmation through price action.

### Example

If you see 78% long positioning in the DAX AND the market is at an important resistance level, the chances for a correction increase.
      `,
      category: 'Strategy',
      datePublished: '2026-01-25',
      dateModified: '2026-01-30',
      readTime: '6 min',
      featured: false
    }
];

const attachTranslations = (dePosts, enPosts) => {
  const de = dePosts.map((post, index) => ({
    ...post,
    translations: {
      de: post.slug,
      en: enPosts[index] ? enPosts[index].slug : null
    }
  }));

  const en = enPosts.map((post, index) => ({
    ...post,
    translations: {
      de: dePosts[index] ? dePosts[index].slug : null,
      en: post.slug
    }
  }));

  return { de, en };
};

const basePaired = attachTranslations(baseArticlesDE, baseArticlesEN);
const articles2026Paired = attachTranslations(articles2026DE, articles2026EN);
const articles2026bPaired = attachTranslations(articles2026bDE, articles2026bEN);
const articles2027Paired = attachTranslations(articles2027DE, articles2027EN);

// Alle Artikel zusammenführen und nach Datum sortieren
export const blogPosts = {
  de: [
    ...basePaired.de,
    ...articles2026Paired.de,
    ...articles2026bPaired.de,
    ...articles2027Paired.de
  ]
    .sort((a, b) => a.datePublished.localeCompare(b.datePublished)),
  en: [
    ...basePaired.en,
    ...articles2026Paired.en,
    ...articles2026bPaired.en,
    ...articles2027Paired.en
  ]
    .sort((a, b) => a.datePublished.localeCompare(b.datePublished))
};

// Helper: Check if post is published (publishDate <= today)
const isPublished = (post) => {
  const today = new Date().toISOString().split('T')[0];
  return post.datePublished <= today;
};

export const getPostBySlug = (slug, lang = 'de') => {
  const posts = blogPosts[lang] || blogPosts.de;
  const post = posts.find(post => post.slug === slug);
  return post && isPublished(post) ? post : null;
};

export const getPostsByLang = (lang = 'de', includeScheduled = false) => {
  const posts = blogPosts[lang] || blogPosts.de;
  if (includeScheduled) return posts;
  return posts.filter(isPublished);
};

export const getFeaturedPosts = (lang = 'de') => {
  const posts = getPostsByLang(lang);
  return posts.filter(post => post.featured);
};

export const getPostsByCategory = (category, lang = 'de') => {
  const posts = getPostsByLang(lang);
  return posts.filter(post => post.category === category);
};

export const getAllCategories = (lang = 'de') => {
  const posts = getPostsByLang(lang);
  return [...new Set(posts.map(post => post.category))];
};

// Get scheduled (future) posts count for admin info
export const getScheduledPostsCount = (lang = 'de') => {
  const allPosts = blogPosts[lang] || blogPosts.de;
  return allPosts.filter(post => !isPublished(post)).length;
};
